/**
 * The dialogs the leads list opens: the New Lead form, the delete confirmation,
 * the bulk actions the API can actually perform, and Change Owner.
 *
 * The form follows `leads.html`'s three fieldsets — Lead Information, Status &
 * Qualification, Company & Assignment — because that grouping is the design
 * reference and because it happens to be the right one: identity, then where
 * the lead is in the process, then who it belongs to.
 *
 * Two prototype fields are deliberately not inputs here:
 *
 * - **Lead Score** is read-only, as it is in the prototype. It is computed by
 *   `cfg.scoring_rule` in `POST /leads` before the row is even inserted, so a
 *   number typed in this form would be overwritten by the response.
 * - **Owner** is absent on create only in the sense that it is a real field —
 *   `POST /leads` takes `owner_id` and honours it. It is `PATCH` that refuses
 *   ownership on an existing lead — that door is `ChangeOwnerModal` below,
 *   which calls `POST /leads/{id}/assign` / `bulk-assign`, not this form.
 */
import { useEffect, useState } from 'react';
import { Modal, Field, ChipFilter } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { scoreClass } from './leadShape.js';

/* -------------------------------------------------------------------------- */
/* New lead                                                                   */
/* -------------------------------------------------------------------------- */

const BLANK = {
  first_name: '', last_name: '', job_title: '', email: '', phone: '',
  company_name: '', website: '', employee_count: '',
  status_id: '', source_id: '', rating: '', estimated_value: '',
  next_followup_at: '', industry_id: '', country_id: '', owner_id: '',
  campaign_id: '', requirement_summary: '',
  interest_type: '', product_id: '', project_name: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/.+/i;

/**
 * The fields worth catching before the round trip: first and last name and
 * company, plus the formats a 422 would reject anyway. Everything else is
 * still the API's call — a client-side mirror of `createLeadBody` would be a
 * second set of rules to keep in step with the first, and the one that
 * drifts is always the copy.
 */
function validateLead(form) {
  const errors = {};
  if (!form.first_name.trim()) errors.first_name = 'First name is required.';
  if (!form.last_name.trim()) errors.last_name = 'Last name is required.';
  if (!form.company_name.trim()) errors.company_name = 'Company is required.';
  if (form.email && !EMAIL_RE.test(form.email)) errors.email = 'Enter a valid email address.';
  if (form.website && !URL_RE.test(form.website)) {
    errors.website = 'Enter a valid URL starting with http:// or https://.';
  }
  if (form.estimated_value !== '' && Number(form.estimated_value) < 0) {
    errors.estimated_value = 'Value cannot be negative.';
  }
  if (form.employee_count !== '' && Number(form.employee_count) < 0) {
    errors.employee_count = 'Employees cannot be negative.';
  }
  return errors;
}

/** `2026-09-05T14:30`, the shape a `datetime-local` input wants, from an ISO string. */
function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Create a lead, or edit one.
 *
 * `lead` (a `toRow()`-shaped record) switches the mode. Owner is dropped
 * entirely in edit mode rather than shown disabled: `PATCH /leads/{id}`
 * `.strict()`-rejects an `owner_id` key outright (ownership is
 * `POST /leads/{id}/assign`'s job, not a field save's), so there is nothing
 * correct a submitted value could do.
 */
export function LeadFormModal({ open, onClose, onSubmit, busy, options, lead }) {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const editing = Boolean(lead);

  /* Reset on open, not on close: leaving the values in place while the dialog
     fades out means the next New Lead starts with the last one's company. */
  useEffect(() => {
    if (!open) return;
    if (lead) {
      setForm({
        first_name: lead.raw?.first_name ?? '',
        last_name: lead.raw?.last_name ?? '',
        job_title: lead.title ?? '',
        email: lead.email ?? '',
        phone: lead.phone ?? '',
        company_name: lead.company ?? '',
        website: lead.website ?? '',
        employee_count: lead.employees ?? '',
        status_id: lead.raw?.status?.id ?? '',
        source_id: lead.raw?.source?.id ?? '',
        rating: lead.rating ?? '',
        estimated_value: lead.value ?? '',
        next_followup_at: toLocalInputValue(lead.followupAt),
        industry_id: lead.raw?.industry?.id ?? '',
        country_id: lead.raw?.country?.id ?? '',
        campaign_id: lead.raw?.campaign?.id ?? '',
        requirement_summary: lead.qualification?.requirement_summary ?? '',
        interest_type: lead.raw?.custom?.interest_type ?? '',
        product_id: lead.raw?.custom?.product_id ?? '',
        project_name: lead.raw?.custom?.project_name ?? '',
      });
    } else {
      setForm({
        ...BLANK,
        status_id: options.defaultStatusId ?? options.statuses[0]?.value ?? '',
      });
    }
    setErrors({});
  }, [open, lead, options.defaultStatusId, options.statuses]);

  const set = (k) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  /* Switching between Product and Project drops whatever the other one held —
     leads.validation.js's superRefine rejects product_id/project_name being
     set alongside the wrong interest_type, so a stale value from before the
     switch would otherwise fail validation on save. */
  const setInterestType = (value) => {
    setForm((f) => ({ ...f, interest_type: value, product_id: '', project_name: '' }));
  };

  const submit = (e) => {
    e.preventDefault();

    const nextErrors = validateLead(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    /* Empty strings are not "no value" to a Zod schema expecting a uuid or a
       number — they are a type error. Everything blank is dropped instead. */
    const payload = {};
    for (const [k, v] of Object.entries(form)) {
      if (v === '' || v == null) continue;
      if (k === 'employee_count') payload[k] = Number(v);
      else if (k === 'estimated_value') payload[k] = Number(v);
      else if (k === 'next_followup_at') payload[k] = new Date(v).toISOString();
      else payload[k] = v;
    }
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Lead' : 'New Lead'}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="leadForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : editing ? 'Save changes' : 'Save lead'}
          </button>
        </>
      }
    >
      <form id="leadForm" onSubmit={submit} noValidate>
        <div className="eyebrow mb-2">Lead Information</div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <Field label="First name" required error={errors.first_name}>
              <input className="form-control" value={form.first_name} onChange={set('first_name')} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Last name" required error={errors.last_name}>
              <input className="form-control" value={form.last_name} onChange={set('last_name')} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Company" required error={errors.company_name}>
              <input className="form-control" value={form.company_name} onChange={set('company_name')} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Title"><input className="form-control" value={form.job_title} onChange={set('job_title')} /></Field>
          </div>
          <div className="col-md-6">
            <Field label="Email" error={errors.email}>
              <input className="form-control" type="email" value={form.email} onChange={set('email')} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Phone"><input className="form-control" value={form.phone} onChange={set('phone')} /></Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Status &amp; Qualification</div>
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <Field
              label="Lead status"
              help={editing ? undefined : 'A lead is qualified after creation, once its checklist is answered.'}
            >
              <select className="form-select" value={form.status_id} onChange={set('status_id')}>
                {/* New leads cannot start Qualified — the API refuses it without the checklist
                    answers, and this form does not collect them. Editing offers it: choosing it
                    opens the checklist on save. */}
                {options.statuses
                  .filter((o) => editing || o.label !== 'Qualified')
                  .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Lead source">
              <Picker value={form.source_id} onChange={set('source_id')} options={options.sources} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Rating">
              <select className="form-select" value={form.rating} onChange={set('rating')}>
                <option value="">Let the score decide</option>
                {['Hot', 'Warm', 'Cold'].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Estimated value" error={errors.estimated_value}>
              <input className="form-control" type="number" min="0" step="1000"
                     value={form.estimated_value} onChange={set('estimated_value')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field
              label="Lead score"
              help={editing ? 'Recomputed on the model’s own schedule, not by this save.' : 'Computed from the active scoring rules when the lead is created.'}
            >
              <div className="d-flex align-items-center gap-2" style={{ height: 37 }}>
                <span className={`score ${editing ? scoreClass(lead.score) : 'score-cold'}`}>{editing ? (lead.score ?? '—') : '—'}</span>
                {!editing && <span className="t-2xs ink-4">set on save</span>}
              </div>
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Next follow-up">
              <input className="form-control" type="datetime-local"
                     value={form.next_followup_at} onChange={set('next_followup_at')} />
            </Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Interest</div>
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <Field label="What is this lead for?" help="Optional — leave unset if it isn't known yet.">
              <ChipFilter
                value={form.interest_type}
                onChange={setInterestType}
                options={[{ value: 'product', label: 'Product' }, { value: 'project', label: 'Project' }]}
              />
            </Field>
          </div>
          {form.interest_type === 'product' && (
            <div className="col-md-8">
              <Field label="Product" error={errors.product_id}>
                <Picker value={form.product_id} onChange={set('product_id')} options={options.products} />
              </Field>
            </div>
          )}
          {form.interest_type === 'project' && (
            <div className="col-md-8">
              <Field label="Project name" error={errors.project_name}>
                <input className="form-control" value={form.project_name} onChange={set('project_name')}
                       placeholder="e.g. Warehouse automation rollout" />
              </Field>
            </div>
          )}
        </div>

        <div className="eyebrow mb-2">Company &amp; Assignment</div>
        <div className="row g-3">
          <div className="col-md-4">
            <Field label="Industry">
              <Picker value={form.industry_id} onChange={set('industry_id')} options={options.industries} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Employees" error={errors.employee_count}>
              <input className="form-control" type="number" min="0"
                     value={form.employee_count} onChange={set('employee_count')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Country">
              <Picker value={form.country_id} onChange={set('country_id')} options={options.countries} />
            </Field>
          </div>
          {!editing && (
            <div className="col-md-6">
              <Field
                label="Owner"
                help="Leave empty to drop the lead into the unassigned queue. Ownership cannot be changed afterwards — POST /leads/{id}/assign is not implemented."
              >
                <Picker value={form.owner_id} onChange={set('owner_id')} options={options.owners} blank="Unassigned queue" />
              </Field>
            </div>
          )}
          <div className="col-md-6">
            <Field label="Website" error={errors.website}>
              <input className="form-control" type="url" placeholder="https://…"
                     value={form.website} onChange={set('website')} />
            </Field>
          </div>
          <div className="col-12">
            <Field label="Requirement summary">
              <textarea className="form-control" rows={2}
                        value={form.requirement_summary} onChange={set('requirement_summary')} />
            </Field>
          </div>
        </div>
      </form>
    </Modal>
  );
}

const Picker = ({ value, onChange, options, blank = 'Not set' }) => (
  <select className="form-select" value={value} onChange={onChange}>
    <option value="">{blank}</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

/* -------------------------------------------------------------------------- */
/* Delete                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Delete, with the reason the database insists on.
 *
 * `iam.app_operation.requires_reason` is true for `delete`, so this is not a
 * courtesy field — the write fails without it. Saying so in the help text stops
 * the reason reading as an optional note somebody can skip.
 */
export function DeleteLeadModal({ open, rows, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');

  useEffect(() => { if (open) setReason(''); }, [open]);

  const many = rows.length > 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={many ? `Delete ${rows.length} leads` : 'Delete lead'}
      footer={
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-delete btn-sm-x"
            disabled={!reason.trim() || busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy ? <><ButtonSpinner />Deleting…</> : (many ? `Delete ${rows.length} leads` : 'Delete')}
          </button>
        </>
      }
    >
      <p className="t-sm ink-2">
        {many
          ? `${rows.length} leads will be soft-deleted.`
          : <>Deleting <strong>{rows[0]?.name}</strong> ({rows[0]?.lead_no}).</>}
        {' '}The rows stay in the database with <code>deleted_at</code> set, and
        {' '}<code>POST /leads/{'{id}'}/restore</code> brings them back.
      </p>
      <Field
        label="Reason"
        required
        help="Written to the audit trail. The delete operation is marked requires_reason in iam.app_operation, so the database rejects the write without one."
      >
        <input
          className="form-control"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Duplicate of LD-00171"
          autoFocus
        />
      </Field>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Bulk actions                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Change status, or add to a campaign, across a selection.
 *
 * One dialog for both because they are the same shape — pick one value, apply
 * it N times — and because both are honest about the cost: the count is in the
 * button, and the note says it is N separate writes. Change Owner looks similar
 * but is not built on this: it has its own dialog below and its own real bulk
 * endpoint, since `POST /leads/bulk-assign` (unlike status/campaign) is live.
 */
export function BulkPatchModal({ open, field, rows, options, onClose, onConfirm, busy }) {
  const [value, setValue] = useState('');

  useEffect(() => { if (open) setValue(''); }, [open, field]);

  const title = field === 'status_id' ? 'Change status' : 'Add to campaign';
  const label = field === 'status_id' ? 'New status' : 'Campaign';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-save btn-sm-x"
            disabled={!value || busy}
            onClick={() => onConfirm({ [field]: value })}
          >
            {busy
              ? <><ButtonSpinner />Applying…</>
              : `Apply to ${rows.length} lead${rows.length === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <Field label={label} required>
        <select className="form-select" value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">Choose one…</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <div className="t-2xs ink-4 mt-2">
        <i className="bi bi-info-circle me-1" aria-hidden="true" />
        Applied one lead at a time — §4&rsquo;s bulk endpoint is not implemented, so
        this is {rows.length} separate audited writes. Any that refuse are reported
        back rather than rolled back.
      </div>
    </Modal>
  );
}

/**
 * Change ownership on one or several leads — `POST /leads/{id}/assign` /
 * `bulk-assign`, gated `leads:assign`. Only rendered where the caller already
 * holds that permission (`LeadsListPage.jsx`'s `canAssign`), so this dialog
 * itself does not re-check it; the button that opens it is the gate.
 */
export function ChangeOwnerModal({ open, rows, options, onClose, onConfirm, busy }) {
  const [ownerId, setOwnerId] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => { if (open) { setOwnerId(''); setReason(''); } }, [open]);

  const many = rows.length > 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={many ? `Change owner — ${rows.length} leads` : 'Change owner'}
      footer={
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-save btn-sm-x"
            disabled={!ownerId || busy}
            onClick={() => onConfirm({ ownerId, reason: reason.trim() || undefined })}
          >
            {busy
              ? <><ButtonSpinner />Reassigning…</>
              : `Reassign ${rows.length} lead${rows.length === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <p className="t-sm ink-2">
        {many
          ? `${rows.length} leads will be reassigned to the owner you choose.`
          : <>Reassigning <strong>{rows[0]?.name}</strong> ({rows[0]?.lead_no}), currently owned by{' '}
              <strong>{rows[0]?.owner ?? 'nobody — unassigned queue'}</strong>.</>}
      </p>
      <Field label="New owner" required>
        <select className="form-select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} autoFocus>
          <option value="">Choose one…</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Reason" help="Optional. Recorded on the audited assignment, not shown to the new owner.">
        <input
          className="form-control"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Territory realignment"
        />
      </Field>
    </Modal>
  );
}

/**
 * Confirm before clearing owner_id — a real, audited write, not something a
 * stray click on the `⋯` menu should trigger unprompted. The New-only rule
 * itself is enforced server-side (`leads.service.js#assignLead`); this dialog
 * just makes sure the caller meant to send whatever they selected back to the
 * unassigned queue, then reports per-lead refusals the same way the toast
 * already does for a mixed selection.
 */
export function TransferToQueueModal({ open, rows, onClose, onConfirm, busy }) {
  const many = rows.length > 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={many ? `Transfer to queue — ${rows.length} leads` : 'Transfer to queue'}
      footer={
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-save btn-sm-x"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy
              ? <><ButtonSpinner />Transferring…</>
              : `Transfer ${rows.length} lead${rows.length === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <p className="t-sm ink-2">
        {many
          ? <>{rows.length} leads will be cleared of their current owner and returned to the unassigned queue.</>
          : <><strong>{rows[0]?.name}</strong> ({rows[0]?.lead_no}) will be cleared of its current owner
              ({rows[0]?.owner ?? 'nobody — already unassigned'}) and returned to the unassigned queue.</>}
      </p>
      <div className="t-2xs ink-4 mt-2">
        <i className="bi bi-info-circle me-1" aria-hidden="true" />
        Only leads still in <strong>New</strong> status can go back to the queue — anything already worked
        refuses on its own line rather than blocking the rest of the selection.
      </div>
    </Modal>
  );
}

/**
 * Why a bar button is disabled, in a dialog rather than a tooltip.
 *
 * Change Owner and Send Email look like the rest of the bar in `leads.html`, and
 * leaving them out entirely makes the port look unfinished for a reason the
 * reader cannot see. A greyed button with a title attribute is better, but the
 * explanation is two sentences long and a tooltip is not where two sentences go.
 */
export function NotBuiltModal({ open, action, onClose }) {
  if (!action) return null;
  return (
    <Modal open={open} onClose={onClose} title={action.title}
           footer={<button type="button" className="btn btn-brand btn-sm-x" onClick={onClose}>Close</button>}>
      <p className="t-sm ink-2">{action.why}</p>
      <div className="t-xs ink-3">
        Endpoint: <code>{action.endpoint}</code> — {action.section}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Columns                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Show and hide columns, the prototype's `#colModal`.
 *
 * The choice lives in `localStorage`, so it is this browser's and nobody
 * else's. The server-side home for it would be `iam.app_user.ui_prefs`, which
 * exists as a column and has no endpoint that writes it; when one lands this
 * dialog changes where it saves and nothing else.
 *
 * Selection, Name and the row menu are not offered. Hiding the name leaves rows
 * with no way to tell them apart, and hiding the menu removes the only route to
 * Delete — a preference that can break the screen is not a preference.
 */
export function ColumnsModal({ open, all, fixed, hidden, onChange, onClose }) {
  const optional = all.filter((c) => !fixed.has(c.key));
  const shownCount = optional.length - hidden.length;

  const toggle = (key) => {
    onChange(hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key]);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit columns"
      footer={
        <>
          <button type="button" className="btn btn-ghost btn-sm-x"
                  disabled={hidden.length === 0} onClick={() => onChange([])}>
            Show all
          </button>
          <button type="button" className="btn btn-brand btn-sm-x" onClick={onClose}>Done</button>
        </>
      }
    >
      <div className="t-xs ink-3 mb-3">
        {shownCount} of {optional.length} optional columns shown. Name and the row
        menu always stay — they are how a row is identified and acted on.
      </div>
      <div className="d-flex flex-column gap-1">
        {optional.map((c) => (
          <label key={c.key} className="form-check d-flex align-items-center gap-2 mb-0 py-1">
            <input
              className="form-check-input mt-0"
              type="checkbox"
              checked={!hidden.includes(c.key)}
              onChange={() => toggle(c.key)}
            />
            <span className="t-sm">{c.header}</span>
          </label>
        ))}
      </div>
      <div className="t-2xs ink-4 mt-3">
        Remembered in this browser only — the per-user store for it
        (<code>iam.app_user.ui_prefs</code>) has no endpoint yet.
      </div>
    </Modal>
  );
}
